import React from "react";
import { Box, Container, Typography, Divider } from "@mui/material";

const Section = ({ title, children }) => (
  <Box sx={{ mb: 6 }}>
    <Typography
      variant="h5"
      fontWeight={700}
      sx={{ mb: 2 }}
    >
      {title}
    </Typography>

    <Box
      sx={{
        color: "primary",
        lineHeight: 1.9,
        fontSize: "1rem",
      }}
    >
      {children}
    </Box>
  </Box>
);

const Terms = () => {
  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        backgroundColor: "#f8fafc",
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box mb={8}>
          <Typography
            variant="h3"
            fontWeight={800}
            color = "primary"
            gutterBottom
          >
            Terms of Service
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ lineHeight: 1.9 }}
          >
            Welcome to MySchoolling™ (“Company”, “we”, “our”, “us”).
            These Terms of Service (“Terms”) govern your access to and use
            of our SaaS-based web and mobile platform (the “Service”).
            <br /><br />
            By registering for or using the Service, you agree to comply
            with these Terms. If you do not agree, you must not use the Service.
          </Typography>
        </Box>

        <Divider sx={{ mb: 8 }} />

        {/* Sections */}

        <Section title="1. Eligibility">
          <ul>
            <li>You must be at least 18 years old to register independently.</li>
            <li>
              If under 18, your account must be created and managed by a
              parent, guardian, or educational institution.
            </li>
            <li>
              By using the Service, you confirm you have the legal authority
              to enter into these Terms.
            </li>
          </ul>
        </Section>

        <Section title="2. Accounts & Registration">
          <ul>
            <li>
              Accounts may be provisioned by schools for administrators,
              teachers, parents, and students.
            </li>
            <li>You agree to provide accurate and up-to-date information.</li>
            <li>
              You are responsible for safeguarding your login credentials.
            </li>
            <li>
              You must notify us immediately of unauthorized access.
            </li>
          </ul>
        </Section>

        <Section title="3. Acceptable Use">
          <Typography sx={{ mb: 2 }}>
            You agree to use the Service only for lawful educational
            and administrative purposes.
          </Typography>
          <ul>
            <li>No hacking, reverse engineering, or bypassing security.</li>
            <li>No uploading unlawful, harmful, or obscene content.</li>
            <li>No fraudulent transactions or impersonation.</li>
            <li>No misuse affecting other users or institutions.</li>
          </ul>
        </Section>

        <Section title="4. Subscriptions & Payments">
          <ul>
            <li>
              Certain features require paid subscriptions.
            </li>
            <li>
              Payments must be made via authorized payment gateways.
            </li>
            <li>
              Subscription fees are non-refundable unless required by law.
            </li>
            <li>
              Pricing may be revised with prior notice.
            </li>
          </ul>
        </Section>

        <Section title="5. Data & Privacy">
          <ul>
            <li>
              Use of the Service is governed by our Privacy Policy.
            </li>
            <li>
              Schools act as data controllers for student data they upload.
            </li>
            <li>
              MySchoolling™ acts as a data processor in compliance with
              the DPDP Act, 2023 and SPDI Rules, 2011.
            </li>
          </ul>
        </Section>

        <Section title="6. Intellectual Property">
          <ul>
            <li>
              All platform software, trademarks, and materials remain
              the property of the Company or licensors.
            </li>
            <li>
              Users receive a limited, non-exclusive, non-transferable
              license to use the Service for educational purposes only.
            </li>
            <li>
              Unauthorized copying, resale, or modification is prohibited.
            </li>
          </ul>
        </Section>

        <Section title="7. User Content">
          <ul>
            <li>
              Schools and users retain ownership of uploaded content.
            </li>
            <li>
              By uploading, you grant us a limited license to host and
              process content solely to provide the Service.
            </li>
          </ul>
        </Section>

        <Section title="8. Service Availability">
          <ul>
            <li>
              We strive for uninterrupted service but do not guarantee it.
            </li>
            <li>
              Maintenance, upgrades, or technical issues may result
              in temporary suspension.
            </li>
            <li>
              Features and pricing may change over time.
            </li>
          </ul>
        </Section>

        <Section title="9. Termination">
          <Typography sx={{ mb: 2 }}>
            We may suspend or terminate accounts if you:
          </Typography>
          <ul>
            <li>Breach these Terms,</li>
            <li>Engage in fraudulent or harmful conduct,</li>
            <li>Misuse the Service affecting others.</li>
          </ul>
          <Typography sx={{ mt: 2 }}>
            Upon termination, access ends immediately. Institutions may
            request data export before closure.
          </Typography>
        </Section>

        <Section title="10. Limitation of Liability">
          <ul>
            <li>
              We are not liable for indirect or consequential damages.
            </li>
            <li>
              We are not responsible for third-party breaches or
              institutional misuse.
            </li>
            <li>
              Total liability shall not exceed subscription fees paid
              in the previous 12 months.
            </li>
          </ul>
        </Section>

        <Section title="11. Indemnification">
          <Typography>
            You agree to indemnify and hold harmless MySchoolling™,
            its officers and employees against claims arising from:
          </Typography>
          <ul>
            <li>Your misuse of the Service,</li>
            <li>Violation of these Terms,</li>
            <li>Infringement of third-party rights.</li>
          </ul>
        </Section>

        <Section title="12. Governing Law">
          <Typography>
            These Terms are governed by the laws of India. Disputes
            shall be subject to the exclusive jurisdiction of the courts
            in Uttar Pradesh, India.
          </Typography>
        </Section>

        <Section title="13. Updates to Terms">
          <Typography>
            We may update these Terms periodically. Continued use of the
            Service constitutes acceptance of the updated Terms.
          </Typography>
        </Section>

        <Section title="14. Contact Information">
          <Typography sx={{ fontWeight: 600, mb: 1 }}>
            MySchoolling™
          </Typography>
          <Typography>
            Email:{" "}
            <a href="mailto:support@myschoolling.com">
              support@myschoolling.com
            </a>
          </Typography>
          <Typography>
            Phone: +91-9458693639, +91-8700007517
          </Typography>
          <Typography>
            Address: 93/295/21, RDA Colony, Indira Nagar,
            Raebareli – 229001, Uttar Pradesh, India
          </Typography>
        </Section>

        <Divider sx={{ mt: 8 }} />

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mt: 3 }}
        >
          These Terms are drafted in accordance with applicable Indian laws,
          including the Information Technology Act, 2000 and the Digital
          Personal Data Protection Act, 2023.
        </Typography>
      </Container>
    </Box>
  );
};

export default Terms;